import React, { useCallback, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@material-ui/core';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import FilterListIcon from '@material-ui/icons/FilterList';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { HeaderText, StickyCell } from '../DispatchUnassigned/blocks';
import { colors } from '@/styles';

const ResizeHandle = styled.div`
    display: inline-block;
    width: 5px;
    height: 100%;
    position: absolute;
    right: 0;
    top: 0;
    cursor: col-resize;
    z-index: 1;
    touch-action: none;
    user-select: none;

    &:hover,
    &.isResizing {
        background: ${colors.greens.primary};
    }

    &::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 2px;
        width: 1px;
        background: #ccc;
    }

    &:hover::after,
    &.isResizing::after {
        background: ${colors.greens.primary};
    }
`;

export default function HeaderCell({
    cell,
    callbacks = {},
    isAllChecked,
    enableResizing = true,
    dragHandleProps,
    onClearSorting,
    isColumnFilterable = () => false,
    onOpenFilter,
    onHideColumn,
    disableColumnMenu = false,
    ...props
}) {
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const isMenuOpen = Boolean(menuAnchorEl);

    const openMenu = useCallback((e) => {
        e.stopPropagation();
        setMenuAnchorEl(e.currentTarget);
    }, []);

    const closeMenu = useCallback((e) => {
        e?.stopPropagation?.();
        setMenuAnchorEl(null);
    }, []);

    const isSelection = cell.id === 'selection';
    const showColumnMenu = useMemo(() => !isSelection && !disableColumnMenu, [isSelection, disableColumnMenu]);

    const menuItemCss = useMemo(
        () => css`
            padding-top: 6px;
            padding-bottom: 6px;
        `,
        []
    );

    const menuItemDividerCss = useMemo(
        () => css`
            border-bottom: 1px solid #e0e0e0;
        `,
        []
    );

    const menuItemIconCss = useMemo(
        () => css`
            min-width: 28px;
            margin-right: 4px;
        `,
        []
    );

    const onSortAsc = useCallback(
        (e) => {
            e.stopPropagation();
            if (!cell.canSort) return;
            // react-table: toggleSortBy(desc?: boolean, isMulti?: boolean)
            cell.toggleSortBy(false);
            closeMenu();
        },
        [cell, closeMenu]
    );

    const onSortDesc = useCallback(
        (e) => {
            e.stopPropagation();
            if (!cell.canSort) return;
            cell.toggleSortBy(true);
            closeMenu();
        },
        [cell, closeMenu]
    );

    const onClearSort = useCallback(
        (e) => {
            e.stopPropagation();
            onClearSorting?.();
            closeMenu();
        },
        [onClearSorting, closeMenu]
    );

    const showSortActions = Boolean(cell.canSort);
    const showSortAsc = showSortActions && (!cell.isSorted || cell.isSortedDesc);
    const showSortDesc = showSortActions && (!cell.isSorted || !cell.isSortedDesc);
    const showClearSort = showSortActions && Boolean(cell.isSorted);
    const showSortSectionDivider = showSortActions && (showSortAsc || showSortDesc || showClearSort);
    const isFilterable = isColumnFilterable(cell.id);

    const content = (
        <StickyCell
            align="left"
            css={css`
                /* Make the header cell fill its flex item wrapper */
                display: flex;
                align-items: stretch;
                width: 100%;
                height: 100%;
                box-sizing: border-box;
                position: relative;
                background-color: white;
            `}
            {...props}
        >
            <div
                css={css`
                    display: flex;
                    align-items: center;
                    justify-content: ${isSelection ? 'center' : 'flex-start'};
                    flex: 1 1 auto;
                    width: 100%;
                    box-sizing: border-box;
                    min-width: 0;
                `}
            >
                {isSelection ? (
                    cell.render('Header', { callbacks, isAllChecked })
                ) : (
                    <>
                        <div
                            css={css`
                                display: flex;
                                align-items: center;
                                min-width: 0;
                                flex: 1 1 auto;
                            `}
                            {...dragHandleProps}
                        >
                            <HeaderText
                                css={css`
                                    white-space: nowrap;
                                    overflow: hidden;
                                    text-overflow: ellipsis;
                                    min-width: 0;
                                    flex: 1 1 auto;
                                `}
                            >
                                {cell.render('Header', { callbacks, isAllChecked })}
                            </HeaderText>
                        </div>

                        {showColumnMenu && (
                            <>
                                <IconButton
                                    aria-label="column menu"
                                    size="small"
                                    onClick={openMenu}
                                    css={css`
                                        margin-left: 4px;
                                        padding: 4px;
                                    `}
                                >
                                    <MoreVertIcon fontSize="small" />
                                </IconButton>

                                <Menu
                                    anchorEl={menuAnchorEl}
                                    open={isMenuOpen}
                                    onClose={closeMenu}
                                    keepMounted
                                    getContentAnchorEl={null}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                >
                                    {showSortAsc && (
                                        <MenuItem
                                            onClick={onSortAsc}
                                            dense
                                            css={[
                                                menuItemCss,
                                                !showSortDesc && !showClearSort && showSortSectionDivider
                                                    ? menuItemDividerCss
                                                    : null,
                                            ]}
                                        >
                                            <ListItemIcon css={menuItemIconCss}>
                                                <ArrowUpwardIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText primary="Sort Ascending" />
                                        </MenuItem>
                                    )}

                                    {showSortDesc && (
                                        <MenuItem
                                            onClick={onSortDesc}
                                            dense
                                            css={[
                                                menuItemCss,
                                                !showClearSort && showSortSectionDivider ? menuItemDividerCss : null,
                                            ]}
                                        >
                                            <ListItemIcon css={menuItemIconCss}>
                                                <ArrowDownwardIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText primary="Sort Descending" />
                                        </MenuItem>
                                    )}

                                    {showClearSort && (
                                        <MenuItem onClick={onClearSort} dense css={[menuItemCss, menuItemDividerCss]}>
                                            <ListItemIcon css={menuItemIconCss}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText primary="Clear Sorting" />
                                        </MenuItem>
                                    )}

                                    <MenuItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onHideColumn?.(cell.id, cell);
                                            closeMenu();
                                        }}
                                        dense
                                        css={[menuItemCss, menuItemDividerCss]}
                                    >
                                        <ListItemIcon css={menuItemIconCss}>
                                            <VisibilityOffIcon fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText primary="Hide Column" />
                                    </MenuItem>

                                    {isFilterable && (
                                        <MenuItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onOpenFilter?.(cell.id);
                                                closeMenu();
                                            }}
                                            dense
                                            css={menuItemCss}
                                        >
                                            <ListItemIcon css={menuItemIconCss}>
                                                <FilterListIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText primary="Filter" />
                                        </MenuItem>
                                    )}
                                </Menu>
                            </>
                        )}
                    </>
                )}
            </div>
            {enableResizing && cell.canResize && (
                <ResizeHandle
                    {...cell.getResizerProps()}
                    className={cell.isResizing ? 'isResizing' : ''}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                />
            )}
        </StickyCell>
    );

    return (
        <>
            {cell.tooltip ? (
                <Tooltip title={cell.tooltip} placement="top" arrow>
                    {content}
                </Tooltip>
            ) : (
                <>{content}</>
            )}
        </>
    );
}
